'use strict';

import Vue from 'vue'
import axios from 'axios';
import { get, isString } from 'lodash';
import {initialiseElement} from '../modules/scroller';
import {DateTime} from 'luxon';
import image from './services/image-manipulator';

const SELECTOR = '.site-search';

(function () {
    const element = document.querySelector(SELECTOR);

    if (element === null) {
        return false;
    }

    const options = {
        engineKey: element.dataset.searchEngineKey,
        searchPageUrl: element.dataset.searchPageUrl,
        resultsPerPage: parseInt(element.dataset.searchResultsPerPage, 10) || 15,
        queryParam: element.dataset.searchQueryParam || 'q',
        pageParam: element.dataset.searchQueryParam || 'page',
        activeTabParam: element.dataset.searchQueryParam || 'show',
        inputSelector: element.dataset.searchInputSelector || null,
    }

    const now = new Date();
    new Vue({
        el: SELECTOR,
        data: {
            loading: true,
            tabs: [],
            results: [],
            upcoming_slider: [],
            pagination: [],
            query: null,
            suggestion: null,
            usingSuggestion: false,
            suggestionLink: null,
            currentPage: null,
            totalPages: null,
            totalResults: 0,
        },
        created: function () {
            this.query = this.getQuery();

            if (!this.query) {
                return (this.loading = false);
            }

            this.getResults(this.query).then(queryResponse => {
                const data = queryResponse.data;

                this.suggestion = get(data, 'info.page.spelling_suggestion.text', null);

                this.updateTabs();

                this.updateInput();

                if (data.record_count) {
                    return this.showResults(data);
                }

                if (this.suggestion && !data.record_count) {
                    return this.getResults(this.suggestion).then(suggestionResponse => {
                        return this.showResults(suggestionResponse.data, true);
                    })
                }

                return (this.loading = false);
            });
        },
        methods: {
            getQueryParams: function () {
                return new URLSearchParams(window.location.search);
            },
            getQuery: function () {
                return this.getQueryParams().get(options.queryParam);
            },
            hasPage: function () {
                return this.getQueryParams().has(options.pageParam);
            },
            getPage: function (defaultValue) {
                return parseInt(this.getQueryParams().get(options.pageParam), 10) || defaultValue || 1;
            },
            hasActiveTab: function () {
                return this.getQueryParams().has(options.activeTabParam);
            },
            getActiveTab: function (defaultValue) {
                return this.getQueryParams().get(options.activeTabParam) || defaultValue || 'all';
            },
            makeUrlWithPage: function (query, page) {
                return this.makeUrlWithBoth(query, page, null);
            },
            makeUrlWithTab: function (query, tab) {
                return this.makeUrlWithBoth(query, null, tab);
            },
            makeUrlWithBoth: function (query, page, tab) {
                if (!query) {
                    return '';
                }

                query = query
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');

                page = parseInt(page, 10);

                let params = new URLSearchParams();

                params.append(options.queryParam, query);

                if (page) {
                    params.append(options.pageParam, page);
                }

                if (isString(tab) && tab.length) {
                    params.append(options.activeTabParam, tab);
                } else if (this.hasActiveTab()) {
                    params.append(options.activeTabParam, this.getActiveTab());
                }

                return options.searchPageUrl + '?' + params.toString();
            },
            makeSwiftypeUrl: function (query) {
                let params = {
                    q: query,
                    per_page: options.resultsPerPage,
                    spelling: 'always',
                    engine_key: options.engineKey,
                }

                if (this.hasPage()) {
                    params['page'] = this.getPage();
                }

                params = new URLSearchParams(params);

                params.append('highlight_fields[page][body][size]', 275 + 25); // we'll trim to 275, but give it a buffer
                params.append('highlight_fields[page][body][fallback]', true);

                if (this.hasActiveTab()) {
                    switch (this.getActiveTab()) {
                        case 'program':
                            params.append('filters[page][type][]', 'season');
                            params.append('filters[page][type][]', 'program');
                            params.append('filters[page][type][]', 'special_event');
                            break;
                        case 'dancer':
                            params.append('filters[page][type][]', 'dancer');
                            params.append('filters[page][type][]', 'dancer_list');
                            params.append('filters[page][type][]', 'dancer_rank');
                            break;
                        case 'news':
                            params.append('filters[page][type][]', 'news');
                            params.append('filters[page][type][]', 'press_room');
                            params.append('filters[page][type][]', 'press_release');
                            break;
                        case 'page':
                            params.append('filters[page][type][]', 'ballet');
                            params.append('filters[page][type][]', 'page');
                            params.append('filters[page][type][]', 'ballet_rep');
                            break;
                    }
                }

                // Makes use of the `internal_weight` during search (this means that results with higher weights will appear on earlier pages)
                // See (https://swiftype.com/documentation/site-search/searching/boosting)
                params.append('functional_boosts[page][internal_weight]', 'linear');

                return 'https://api.swiftype.com/api/v1/public/engines/search.json?' + params.toString();
            },
            getResults: function (query) {
                const url = this.makeSwiftypeUrl(query);

                return axios.get(url);
            },
            processResults: function (results) {
                const blocks = [];

                for (let result of results) {
                    if (get(result, 'hidden', false)) {
                        continue;
                    }

                    let relativeUrl = result.url.replace(/^https?:\/\/[^/]+\//, '');
                    result.relative_url = relativeUrl;

                    switch(result.type) {
                        case 'program':
                            if (now < (new Date(result.end_date))) {
                                this.upcoming_slider.push(
                                    this.processProgram(result, results, blocks)
                                );
                            } else {
                                blocks.push(
                                    this.processProgram(result, results, blocks)
                                );
                            }
                            break;
                        case 'dancer':
                            blocks.push(
                                this.processDancer(result, results, blocks)
                            );
                            break;
                        case 'news':
                            blocks.push(
                                this.processNews(result, results, blocks)
                            );
                            break;
                        default:
                            blocks.push(
                                this.processDefault(result, results, blocks)
                            );
                            break;
                    }
                }
                if (this.upcoming_slider.length) {
                    const scroller = this.$el.querySelector('.search-result--scroller');
                    setTimeout(() => {
                        initialiseElement(scroller);
                    }, 500);
                }
                this.results = blocks;
            },
            processProgram: function (result) {
                const cssClasses = {
                    'search-result': true,
                    'search-result--default': true,
                };
                if (result.image && now < (new Date(result.end_date))) {
                    cssClasses['search-result--image'] = true;
                }
                const display_start_date = DateTime.fromISO(result.start_date).toFormat('ccc \'<span>\'LLL d\'</span>\'');
                const display_end_date = DateTime.fromISO(result.end_date).toFormat('ccc \'<span>\'LLL d\'</span>\'');
                return {
                    type: 'program',
                    display_type: 'Past Event',
                    display_image: (result.image && now < (new Date(result.end_date))) ?
                        image.process(result.image, {crop: 'fill', format: 'auto', gravity: 'auto', height: 400, width: 500, quality: 'auto'}) :
                        false,
                    item: result,
                    cssClasses,
                    display_start_date,
                    display_end_date,
                };
            },
            processDancer: function (result) {
                const cssClasses = {
                    'search-result': true,
                    'search-result--dancer': true,
                };
                if (result.image) {
                    cssClasses['search-result--image'] = true;
                }
                return {
                    type: 'dancer',
                    display_type: 'Dancer',
                    display_image: (result.image) ?
                        image.process(result.image, {crop: 'fill', format: 'auto', gravity: 'auto', height: 400, width: 400, quality: 'auto'}) :
                        false,
                    item: result,
                    cssClasses,
                };
            },
            processNews: function (result) {
                const cssClasses = {
                    'search-result': true,
                    'search-result--news': true,
                };
                if (result.image) {
                    cssClasses['search-result--image'] = true;
                }
                return {
                    type: 'news',
                    display_type: 'News',
                    display_image: (result.image) ?
                        image.process(result.image, {crop: 'fill', format: 'auto', gravity: 'auto', height: 400, width: 400, quality: 'auto'}) :
                        false,
                    item: result,
                    cssClasses,
                };
            },
            processDefault: function (result) {
                const cssClasses = {
                    'search-result': true,
                    'search-result--default': true,
                };
                return {
                    type: 'default',
                    display_type: false,
                    item: result,
                    cssClasses,
                };
            },
            buildPagination: function (data) {
                const {
                    total_result_count, per_page, current_page, num_pages, query
                } = data.info.page;

                const start = (current_page - 1) * per_page;

                // index of last item on this page
                const end = Math.min((current_page * per_page) - 1, total_result_count);

                // link to previous page, if exists
                const prev = current_page > 1 ? this.makeUrlWithPage(query, current_page - 1) : null;

                // link to next page, if exists
                const next = current_page < num_pages ? this.makeUrlWithPage(query,  current_page + 1) : null;

                let pages = [];

                for (let i = 1; i <= num_pages; i++) {
                    // skip showing links to pages that aren't either:
                    //  * the first few pages
                    //  * the last page
                    //  * within a range of current page
                    if (i > 5 && i < num_pages - 1 && Math.abs(i - current_page) > 5) {
                        continue;
                    }

                    // the index of the first result on the given page
                    let offset = i * per_page;

                    pages.push({
                        number: i,
                        min: offset,
                        max: Math.min(total_result_count, offset + per_page),
                        is_current: i == current_page,
                        link: this.makeUrlWithPage(query, i)
                    });
                }

                this.currentPage = current_page;
                this.totalPages = num_pages;
                this.totalResults = total_result_count;

                this.pagination = { start, end, prev, next, pages };
            },
            updateInput: function () {
                if (!options.inputSelector) {
                    return;
                }

                const inputElement = document.querySelector(options.inputSelector);

                if (!inputElement) {
                    return;
                }

                inputElement.value = this.query;
            },
            updateTabs: function () {
                const activeTab = this.getActiveTab();

                const tabs = [{
                    slug: 'all',
                    is_active: activeTab === 'all',
                    name: 'All',
                    link: this.makeUrlWithTab(this.query, 'all'),
                }];

                tabs.push({
                    slug: 'program',
                    is_active: activeTab === 'program',
                    name: 'Programs',
                    link: this.makeUrlWithTab(this.query, 'program'),
                });

                tabs.push({
                    slug: 'dancer',
                    is_active: activeTab === 'dancer',
                    name: 'Dancers',
                    link: this.makeUrlWithTab(this.query, 'dancer'),
                });

                tabs.push({
                    slug: 'news',
                    is_active: activeTab === 'news',
                    name: 'News',
                    link: this.makeUrlWithTab(this.query, 'news'),
                });

                tabs.push({
                    slug: 'page',
                    is_active: activeTab === 'page',
                    name: 'Pages',
                    link: this.makeUrlWithTab(this.query, 'page'),
                });

                this.tabs = tabs;
            },
            showResults: function (data, usingSuggestion) {
                this.usingSuggestion = usingSuggestion;

                if (this.suggestion) {
                    this.suggestionLink = this.makeUrlWithBoth(this.suggestion);
                }

                this.buildPagination(data);

                this.processResults(data.records.page);

                this.loading = false;
            },
        },
    });

}());
